/**
 * PATH: packages/email-hub/src/feature/components/email-preview-pane/PreviewWholeHeader.tsx
 * FILE_ID: EMAILHUB-EMAILPREVIEWPANE-WHOLEHEADER-001
 * PURPOSE: Bind preview controller state to the shared reader-header adapter.
 * ROLE IN ARCHITECTURE: Host seam; frame/menu/verb semantics live in reader-header/adapters.
 * MAIN EXPORTS: PreviewWholeHeader.
 * NON-RESPONSIBILITIES: This file does not render the message body or overlays,
 *   and it runs NO mutations — core verbs execute in the shared runtime.
 * NOTES FOR FUTURE AI: Keep this to binding construction only — any action
 *   rendered here without registry ownership breaks preview/full parity.
 */
import {
  PreviewReaderHeaderAdapter,
  type ReaderActionId,
} from '../../reader-header'
import type {
  PreviewHeaderProps,
  PreviewReadingSurfaceProps,
} from './types'

export function PreviewWholeHeader({
  header,
  reading,
}: {
  header: PreviewHeaderProps
  reading: PreviewReadingSurfaceProps
}) {
  const actions = new Map(
    header.previewActions.map((action) => [action.id, action]),
  )
  /** Reuse the proven preview toolbar handlers for host-routed verbs only
   *  (compose navigation, link-job modal). Mutating verbs go through the
   *  shared runtime, never through these legacy handlers. */
  const run = (id: ReaderActionId) => () =>
    actions.get(id)?.onClick(() => undefined)

  return (
    <PreviewReaderHeaderAdapter
      email={header.email}
      narrow={header.navigation?.narrow ?? false}
      backLabel={header.navigation?.narrow ? 'Back to inbox' : 'Close reader'}
      onBack={header.onClose}
      navigation={header.navigation}
      onCompose={(intent) => run(intent)()}
      onFind={() => header.find.open()}
      onExpand={header.onFullView}
      pingOpen={header.pingOpen}
      onPingOpenChange={header.setPingOpen}
      onLinkJob={run('link_job')}
      terminal={{
        beginRemoval: () => {
          // Inbox host: remove + advance now, hand back the reinsert closure.
          if (reading.onBeginTerminalRemoval) {
            return reading.onBeginTerminalRemoval(header.email.id)
          }
          // Hosts without the seam (search page, embeds): resolve without a
          // restore closure — the runtime's reconcile refetch repairs failures.
          reading.onResolved?.(header.email.id)
          return undefined
        },
      }}
      detailError={reading.detailError}
      kanban={{
        api: reading.kanbanApi,
        orgId: reading.kanbanOrgId,
        onChanged: reading.onRefetch,
        onPatchEmail: reading.onPatchEmail,
        onResolved: reading.onResolved,
        openAllocatedCard: reading.openAllocatedCard,
        openAllocatedBoard: reading.openAllocatedBoard,
        openBillDoc: reading.openBillDoc,
        onPeekJob: reading.setPeekJob,
        canRequestRule: reading.canRequestRule,
        onOpenRuleDialog: () => reading.setRuleDialogOpen(true),
        allocationMembers: reading.allocationMembers,
        onFilterCompany: reading.onFilterCompany,
      }}
    />
  )
}
